import { useState, useRef, useEffect } from 'react';

const FilterDropdown = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-600 bg-slate-800/60 text-white text-[11px] font-medium hover:border-cyan-500/50 transition-colors whitespace-nowrap"
      >
        <svg className="w-3 h-3 text-cyan-400 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 4h18v2H3zm2 7h14v2H5zm4 7h6v2H9z" />
        </svg>
        <span>{value}</span>
        <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-slate-900 border border-slate-700 rounded-lg shadow-xl min-w-[190px]">
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs transition-colors first:rounded-t-lg last:rounded-b-lg ${
                value === opt ? 'text-cyan-400 bg-slate-800 font-semibold' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const COLUMN_CONFIGS = {
  HITTING_STANDARD:        ['GP', 'AB', 'H', 'RBI', '2B', '3B', 'HR', 'K', 'BB', 'AVG', 'OBP', 'SLG'],
  HITTING_ADVANCED:        ['ISO', 'wOBA', 'wRC+', 'OPS+', 'K%', 'BB%', 'K/BB', 'BABIP'],
  PITCHING_STANDARD:       ['APP', 'GS', 'IP', 'ERA', 'W', 'L', 'K', 'BB', 'H', 'R', 'ER', 'HR', 'HBP', 'CG', 'SHO'],
  PITCHING_ADVANCED:       ['WHIP', 'FIP', 'K%', 'BB%', 'K-BB%', 'FIP+', 'xFIP', 'ERA+', 'LOB%', 'BAA'],
  FIELDING_STANDARD:       ['GP', 'TC', 'PO', 'A', 'E', 'FLD%', 'DP', 'SBA', 'RCS', 'RCS%', 'PB', 'CI'],
  'BASE RUNNING_STANDARD': ['GP', 'R', 'TB', 'SB', 'CS'],
};

const fmt  = (v) => (!v && v !== 0 ? '0' : v.toString());
const fmtR = (v) => { if (!v && v !== 0) return '0'; const n = Number(v); return isNaN(n) ? '0' : Math.round(n).toString(); };
const fmtD = (v) => {
  if (v === null || v === undefined || v === '-' || v === '') return '0';
  const n = Number(v);
  if (isNaN(n)) return '0';
  if (n >= 1) return n.toFixed(2);
  return n.toFixed(3).substring(1);
};
const fmtP = (v) => {
  if (v === null || v === undefined || v === '-' || v === '') return '0';
  const n = Number(v);
  if (isNaN(n)) return '0';
  return n > 1 ? `${n.toFixed(1)}%` : `${(n * 100).toFixed(1)}%`;
};

const num = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };

const cell = (entry, col, statType) => {
  if (!entry) return '0';
  switch (col) {
    case 'GP':    return fmt(entry.gp || entry.games_played || entry.gamesPlayed || entry.appearances || entry.fielding_games);
    case 'AB':    return fmt(entry.ab || entry.at_bats || entry.atBats);
    case 'H':     return statType === 'PITCHING' ? fmt(entry.h || entry.hits_allowed || entry.hitsAllowed) : fmt(entry.h || entry.hits);
    case 'RBI':   return fmt(entry.rbi);
    case '2B':    return fmt(entry['2b'] || entry.doubles);
    case '3B':    return fmt(entry['3b'] || entry.triples);
    case 'HR':    return statType === 'PITCHING' ? fmt(entry.hr || entry.home_runs_allowed || entry.homeRunsAllowed) : fmt(entry.hr || entry.home_runs || entry.homeRuns);
    case 'K':     return statType === 'PITCHING' ? fmt(entry.so || entry.k || entry.strikeouts_pitched || entry.strikeoutsPitched) : fmt(entry.so || entry.k || entry.strikeouts);
    case 'BB':    return statType === 'PITCHING' ? fmt(entry.bb || entry.walks_allowed || entry.walksAllowed) : fmt(entry.bb || entry.walks);
    case 'AVG':   return fmtD(entry.avg || entry.batting_average || entry.battingAverage);
    case 'OBP':   return fmtD(entry.ob_pct || entry.obp || entry.on_base_percentage || entry.onBasePercentage);
    case 'SLG':   return fmtD(entry.slg || entry.slg_pct || entry.slugging_percentage || entry.sluggingPercentage);
    case 'ISO':   return fmtD(entry.iso);
    case 'wOBA':  return fmtD(entry.woba || entry.wOBA);
    case 'wRC+':  return fmtR(entry.wRC_plus || entry.wrc_plus || entry['wrc+'] || entry.wrcPlus);
    case 'OPS+':  return fmtR(entry.ops_plus || entry['ops+'] || entry.opsPlus);
    case 'K%': {
      // const v = statType === 'PITCHING' ? calcPitcherKPct(entry) : calcHitterKPct(entry);
      // return v == null ? '0' : fmtP(v);
      const v = statType === 'PITCHING' ? entry.K_percent : entry.k_percentage;
      return fmtP(v);
    }
    case 'BB%': {
      // const v = statType === 'PITCHING' ? calcPitcherBBPct(entry) : calcHitterBBPct(entry);
      // return v == null ? '0' : fmtP(v);
      const v = statType === 'PITCHING' ? entry.BB_percent : entry.bb_percentage;
      return fmtP(v);
    }
    case 'K/BB': {
      const k  = num(entry.so || entry.strikeouts || entry.k);
      const bb = num(entry.bb || entry.walks);
      if (bb > 0) return (k / bb).toFixed(2);
      return k > 0 ? '∞' : '0';
    }
    case 'BABIP': return fmtD(entry.babip || entry.BABIP || entry.BABIP_against);
    case 'APP':   return fmt(entry.app || entry.appearances || entry.gp || entry.games_pitched || entry.gamesPitched);
    case 'GS':    return fmt(entry.gs || entry.games_started || entry.gamesStarted);
    case 'IP':    return fmt(entry.ip || entry.innings_pitched || entry.inningsPitched);
    case 'ERA':   { const _e = entry.era || entry.ERA; if (_e === null || _e === undefined || _e === '-' || _e === '') return '0'; const _n = Number(_e); return isNaN(_n) ? '0' : _n.toFixed(2); }
    case 'W':     return fmt(entry.w || entry.wins);
    case 'L':     return fmt(entry.l || entry.losses);
    case 'R':     return statType === 'PITCHING' ? fmt(entry.r || entry.runs_allowed || entry.runsAllowed) : fmt(entry.r || entry.runs);
    case 'ER':    return fmt(entry.er || entry.earned_runs || entry.earnedRuns);
    case 'HBP':   return fmt(entry.hbp || entry.hit_by_pitch || entry.hitByPitch || entry.hit_batters);
    case 'CG':    return fmt(entry.cg || entry.complete_games || entry.completeGames);
    case 'SHO':   return fmt(entry.sho || entry.shutouts);
    case 'WHIP':  { const _w = entry.whip || entry.WHIP; if (_w === null || _w === undefined || _w === '-' || _w === '') return '0'; const _n = Number(_w); return isNaN(_n) ? '0' : _n.toFixed(2); }
    case 'FIP':   return fmtD(entry.FIP ?? entry.fip);
    case 'K-BB%': return fmtP(entry.K_BB_percent ?? entry.k_bb_percent);
    case 'FIP+':  return fmtR(entry.FIP_plus ?? entry.fip_plus ?? entry['fip+'] ?? entry.fipPlus);
    case 'xFIP':  return fmtD(entry.xFIP ?? entry.xfip);
    case 'ERA+':  return fmtR(entry.ERA_plus ?? entry.era_plus ?? entry['era+'] ?? entry.eraPlus);
    case 'LOB%':  return fmtP(entry.LOB_percent ?? entry.lob_percent ?? entry.lob_pct);
    case 'BAA': {
      const h  = num(entry.hits_allowed || entry.hitsAllowed || entry.h);
      const ab = num(entry.at_bats_against || entry.atBatsAgainst || entry.ab);
      if (ab <= 0) return '0';
      return (h / ab).toFixed(3).replace(/^0/, '');
    }
    case 'TC':    return fmt(entry.tc || entry.total_chances || entry.totalChances);
    case 'PO':    return fmt(entry.po || entry.putouts);
    case 'A':     return fmt(entry.a || entry.assists);
    case 'E':     return fmt(entry.e || entry.errors);
    case 'FLD%':  return fmtD(entry.fpct || entry['fld%'] || entry.fld_pct || entry.fielding_percentage || entry.fieldingPercentage);
    case 'DP':    return fmt(entry.dp || entry.double_plays || entry.doublePlays);
    case 'SBA':   return fmt(entry.sba || entry.stolen_bases_against || entry.stolenBasesAllowed);
    case 'RCS':   return fmt(entry.rcs || entry.runners_caught_stealing || entry.runnersCaughtStealing);
    case 'RCS%':  return fmtP(entry.rcs_pct || entry['rcs%'] || entry.rcsPct || entry.runners_caught_stealing_percentage);
    case 'PB':    return fmt(entry.pb || entry.passed_balls || entry.passedBalls);
    case 'CI':    return fmt(entry.ci || entry.catcher_interference || entry.catcherInterference);
    case 'TB':    return fmt(entry.tb || entry.total_bases || entry.totalBases);
    case 'SB':    return fmt(entry.sb || entry.stolen_bases || entry.stolenBases);
    case 'CS':    return fmt(entry.cs || entry.caught_stealing || entry.caughtStealing);
    case 'VS R':  return fmt(entry.vsRHP ?? entry.vs_r ?? entry.vsR ?? entry.avg_vs_r ?? entry.avg_vs_rhp ?? entry.vs_right ?? entry.vsRight);
    case 'VS L':  return fmt(entry.vsLHP ?? entry.vs_l ?? entry.vsL ?? entry.avg_vs_l ?? entry.avg_vs_lhp ?? entry.vs_left ?? entry.vsLeft);
    default:      return '0';
  }
};

const getYear   = (e) => e?.seasonYear || e?.year || e?.season || '-';
const getTeam   = (e, fallbackTeam) => e?._teamOverride || e?.team?.name || e?.teamName || (typeof e?.team === 'string' ? e.team : null) || fallbackTeam || '-';
const getLeague = (e, fallbackLeague) => e?._leagueOverride || e?.league || e?.leagueName || e?.team?.league || fallbackLeague || '-';
const normalize = (arr) => Array.isArray(arr) ? arr : arr ? [arr] : [];

const parseYearStart = (e) => {
  const y = getYear(e);
  if (!y || y === '-') return -Infinity;
  const m = String(y).match(/\d{4}/);
  return m ? parseInt(m[0], 10) : -Infinity;
};
const sortByYearAsc = (arr) => [...arr].sort((a, b) => parseYearStart(a) - parseYearStart(b));

export const StatisticsSectionView = ({ battingStats = [], fieldingStats = [], pitchingStats = [], conferenceData = null, summerData = null, position = '', teamName = null, teamLeague = null }) => {
  const positionUpper = position?.toUpperCase() || '';
  const isPitcher = ['P', 'PITCHER', 'RHP', 'LHP', 'SP', 'RP'].includes(positionUpper);

  const [statType,    setStatType]    = useState(isPitcher ? 'PITCHING' : 'HITTING');
  const [overallStat, setOverallStat] = useState('OVERALL STATS');
  const [standard,    setStandard]    = useState('STANDARD');

  const pickFromSeasons = (seasons, statKey) =>
    sortByYearAsc(
      seasons.map(season => {
        const entry = season[statKey];
        if (!entry) return null;
        return { ...entry, seasonYear: season.seasonYear };
      }).filter(Boolean)
    );

  const pickSummerFromSeasons = (seasons, statKey) =>
    sortByYearAsc(
      seasons.map(season => {
        const entry = season[statKey];
        if (!entry) return null;
        return {
          ...entry,
          seasonYear: season.seasonYear,
          _teamOverride: season.summerTeamName,
          _leagueOverride: season.league,
        };
      }).filter(Boolean)
    );

  const statsData = (() => {
    if (overallStat === 'REGULAR SEASON CONFERENCE') {
      if (!conferenceData?.seasons?.length) return [];
      const seasons = conferenceData.seasons;
      switch (statType) {
        case 'HITTING':      return pickFromSeasons(seasons, 'batting');
        case 'PITCHING':     return pickFromSeasons(seasons, 'pitching');
        case 'FIELDING':     return pickFromSeasons(seasons, 'fielding');
        case 'BASE RUNNING': return pickFromSeasons(seasons, 'batting');
        default:             return pickFromSeasons(seasons, 'batting');
      }
    }
    if (overallStat === 'SUMMER BALL') {
      if (!summerData?.seasons?.length) return [];
      const seasons = summerData.seasons;
      switch (statType) {
        case 'HITTING':      return pickSummerFromSeasons(seasons, 'batting');
        case 'PITCHING':     return pickSummerFromSeasons(seasons, 'pitching');
        case 'FIELDING':     return pickSummerFromSeasons(seasons, 'fielding');
        case 'BASE RUNNING': return pickSummerFromSeasons(seasons, 'batting');
        default:             return pickSummerFromSeasons(seasons, 'batting');
      }
    }
    switch (statType) {
      case 'HITTING':      return sortByYearAsc(normalize(battingStats));
      case 'PITCHING':     return sortByYearAsc(normalize(pitchingStats));
      case 'FIELDING':     return sortByYearAsc(normalize(fieldingStats));
      case 'BASE RUNNING': return sortByYearAsc(normalize(battingStats));
      default:             return sortByYearAsc(normalize(battingStats));
    }
  })();

  const configKey    = `${statType}_${standard}`;
  const baseColumns  = COLUMN_CONFIGS[configKey] || COLUMN_CONFIGS[`${statType}_STANDARD`] || [];
  const columns      = baseColumns;
  const standardOpts = (statType === 'FIELDING' || statType === 'BASE RUNNING') ? ['STANDARD'] : ['STANDARD', 'ADVANCED'];

  return (
    <div className="w-full">
      <h3 className="text-white font-bold text-base sm:text-lg mb-4 text-left">STATISTICS</h3>

      <div className="flex flex-wrap gap-2 mb-4">
        <FilterDropdown
          options={['HITTING', 'PITCHING', 'FIELDING', 'BASE RUNNING']}
          value={statType}
          onChange={(val) => { setStatType(val); if (val === 'FIELDING' || val === 'BASE RUNNING') setStandard('STANDARD'); }}
        />
        <FilterDropdown options={['OVERALL STATS', 'REGULAR SEASON', 'REGULAR SEASON CONFERENCE', 'SUMMER BALL']}   value={overallStat} onChange={setOverallStat} />
        <FilterDropdown options={standardOpts}                                                                 value={standard}    onChange={setStandard} />
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-700/50">
        <table className="w-full text-xs" style={{ minWidth: '520px' }}>
          <thead>
            <tr className="border-b border-slate-700/50 bg-slate-800/40">
              <th className="text-left px-3 py-2.5 text-gray-400 font-semibold whitespace-nowrap">YEAR</th>
              <th className="text-left px-3 py-2.5 text-gray-400 font-semibold whitespace-nowrap">TEAM</th>
              <th className="text-left px-3 py-2.5 text-gray-400 font-semibold whitespace-nowrap">LEAGUE</th>
              {columns.map((col) => (
                <th key={col} className="text-center px-3 py-2.5 text-gray-400 font-semibold whitespace-nowrap">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {statsData.length > 0 ? (
              statsData.map((entry, i) => (
                <tr key={i} className="border-b border-slate-700/30 hover:bg-slate-800/30 transition-colors">
                  <td className="px-3 py-2.5 text-white font-medium whitespace-nowrap">{getYear(entry)}</td>
                  <td className="px-3 py-2.5 text-slate-300 whitespace-nowrap">{getTeam(entry, teamName)}</td>
                  <td className="px-3 py-2.5 text-slate-300 whitespace-nowrap">{getLeague(entry, teamLeague)}</td>
                  {columns.map((col) => (
                    <td key={col} className="px-3 py-2.5 text-center text-white whitespace-nowrap">{cell(entry, col, statType)}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length + 3} className="px-3 py-8 text-center text-slate-500">
                  {overallStat === 'SUMMER BALL'
                    ? 'Summer ball data is not available for this player'
                    : overallStat === 'REGULAR SEASON CONFERENCE'
                      ? 'No conference data available'
                      : 'No statistics available'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StatisticsSectionView;
